import ComplianceBanner from "@/components/ComplianceBanner";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowsClockwise, Truck, Envelope } from "@phosphor-icons/react";

export default function About() {
  return (
    <div className="page-enter">
      <ComplianceBanner />

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-16">
          <div className="overline mb-4">Our story</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
            We built PRMU<br />because <span className="accent-slash">we needed it.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed">
            PRMU exists to raise the standard for research materials: real quality control, honest paperwork, and shipping that actually shows up when we say it will.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] surface-1 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&q=90"
                alt="PRMU"
                className="w-full h-full object-cover opacity-90"
              />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="overline mb-4">Why we exist</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">Quality you can count on. Support you can reach.</h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              We got tired of research suppliers that dodged questions and shipped whenever they felt like it. PRMU flips that: tight quality control, straight answers, and email replies from a real teammate within one business day.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              We're small on purpose. That means we know our customers by name, we know what's in every vial, and we're on the hook if something goes wrong.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-6 stagger">
          {[
            { i: <ShieldCheck size={22} weight="duotone" />, t: "Consistent quality", d: "We publish our spec and hit it, lot after lot. If a batch misses, it doesn't ship." },
            { i: <Truck size={22} weight="duotone" />, t: "Fast, discreet shipping", d: "Order by 3pm, out the door same day. Packaged so it arrives intact and unmarked." },
            { i: <ArrowsClockwise size={22} weight="duotone" />, t: "Easy replacements", d: "Damaged in transit? Wrong item? We replace it. No forms, no fights." },
          ].map((c) => (
            <div key={c.t} className="surface-1 p-8 rounded-2xl">
              <div className="text-blue-400 mb-4">{c.i}</div>
              <div className="font-display-plain text-2xl font-semibold">{c.t}</div>
              <div className="mt-3 text-sm text-zinc-500 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="overline mb-3">Statement of intended use</div>
            <div className="surface-1 p-8 rounded-2xl">
              <p className="text-sm text-zinc-400 leading-relaxed">
                All PRMU products are supplied for research use only. Products are not intended, labeled, or approved for use in humans or animals, and nothing on this site is a medical, therapeutic, or performance claim. We are not a supplement brand, a clinic, or a healthcare provider.
              </p>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Talk to us</div>
            <div className="surface-1 p-8 rounded-2xl">
              <div className="flex items-start gap-3">
                <Envelope size={22} className="text-blue-400 mt-1" />
                <div>
                  <div className="font-display-plain font-semibold">Human support</div>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                    Questions about a lot, a shipment, or a bulk order? Email <span className="font-mono">support@prmu.dev</span> — a real teammate replies within one business day.
                  </p>
                  <Link to="/partners" className="mt-4 inline-flex btn-outline !py-2 !px-4">Partner with PRMU →</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
